import * as React from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { postJSON } from '@/lib/api'

interface VerificationResponse {
  verification: { token: string; person: { name: string } }
}

export function GivingContactVerification({
  email,
  onVerified,
}: {
  email: string
  onVerified: (token: string) => void
}) {
  const [code, setCode] = React.useState('')
  const [requested, setRequested] = React.useState(false)
  const [verified, setVerified] = React.useState(false)
  const request = useMutation({
    mutationFn: () =>
      postJSON<{ received: boolean }>('/api/v1/give/contact_verification', {
        giving_verification: { email },
      }),
    onSuccess: () => setRequested(true),
  })
  const redeem = useMutation({
    mutationFn: () =>
      postJSON<VerificationResponse>('/api/v1/give/contact_verification/redemption', {
        giving_verification: { email, code },
      }),
    onSuccess: (data) => {
      setVerified(true)
      onVerified(data.verification.token)
    },
  })

  return (
    <div className="space-y-2 border-t border-border pt-4">
      <p className="text-body-sm font-medium">Already given with this email?</p>
      <p className="text-caption text-muted-foreground">
        Verify your email to attach this gift to your existing giving history. If the address
        matches a contact, a code is on its way.
      </p>
      {!verified && !requested && (
        <Button
          type="button"
          variant="outline"
          className="h-8 text-body-sm"
          disabled={!email.trim() || request.isPending}
          onClick={() => request.mutate()}
        >
          {request.isPending ? 'Sending code…' : 'Send me a Sign-in Code'}
        </Button>
      )}
      {requested && !verified && (
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            <Label htmlFor="give-verification-code">6-digit code</Label>
            <Input
              id="give-verification-code"
              inputMode="numeric"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-9"
            disabled={code.length !== 6 || redeem.isPending}
            onClick={() => redeem.mutate()}
          >
            {redeem.isPending ? 'Checking…' : 'Verify'}
          </Button>
        </div>
      )}
      {verified && (
        <p className="text-caption text-success">Gift will attach to your existing contact.</p>
      )}
      {redeem.isError && <Alert>The code is invalid or expired</Alert>}
    </div>
  )
}
